import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Settings, Droplets, Shield, Zap } from 'lucide-react';
import { productData } from '../data/productData';
import useSEO from '../hooks/useSEO';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const product = productData[id];

  const from = location.state?.from;
  const savedListState = {
    viewMode: location.state?.viewMode,
    searchTerm: location.state?.searchTerm,
    selectedCategory: location.state?.selectedCategory,
    currentPage: location.state?.currentPage,
    productsPerPage: location.state?.productsPerPage,
    scrollY: location.state?.scrollY,
  };

  const displayTitle = product?.title || product?.name || 'Product Details';

  useSEO({
    title: product ? displayTitle : 'Product Not Found',
    description: product
      ? `${product.shortDescription || product.description || displayTitle} - Minipore water treatment solutions. Request a quote today.`
      : 'Product not found.',
    keywords: product
      ? `${displayTitle}, ${product.category || ''}, Minipore, water treatment`
      : 'Minipore',
    image: product?.image,
    url: `/products/${id}`,
  });

  const goBackToList = () => {
    if (from) { navigate(from, { state: savedListState }); }
    else if (window.history.length > 1) { navigate(-1); }
    else { navigate('/products'); }
  };

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <button onClick={goBackToList} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  const hasComplexSpecs = product.specifications &&
    Object.values(product.specifications).some(value => typeof value === 'object');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button onClick={goBackToList} className="flex items-center text-blue-600 hover:text-blue-800 transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Products
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="p-8 border-b border-gray-200">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
              <div className="flex-1">
                <div className="text-sm text-blue-600 font-medium mb-2">{product.category}</div>
                <h1 className="text-3xl font-bold text-gray-900 mb-4">{displayTitle}</h1>
                <p className="text-lg text-gray-600 mb-6">{product.description}</p>
                <p className="text-gray-700 leading-relaxed">{product.longDescription}</p>
              </div>
              <div className="mt-6 lg:mt-0 lg:ml-8">
                <img
                  src={product.image}
                  alt={displayTitle}
                  className="w-full lg:w-96 h-64 object-cover rounded-lg"
                />
              </div>
            </div>
          </div>

          <div className="p-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <CheckCircle className="w-6 h-6 mr-3 text-green-600" />
              Key Features
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {product.features.map((feature, index) => (
                <div key={index} className="flex items-start">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {product.productFeatures && (
            <div className="p-8 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Zap className="w-6 h-6 mr-3 text-orange-600" />
                Product Features
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {product.productFeatures.map((feature, index) => (
                  <div key={index} className="bg-orange-50 p-6 rounded-lg">
                    <h3 className="text-lg font-bold text-orange-800 mb-3">{feature.title}</h3>
                    <p className="text-gray-700">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="p-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <Settings className="w-6 h-6 mr-3 text-blue-600" />
              Specifications
            </h2>
            {hasComplexSpecs ? (
              <div className="space-y-8">
                {Object.entries(product.specifications).map(([modelName, specs]) => (
                  <div key={modelName} className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{modelName}</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(specs).map(([key, value]) => (
                        <div key={key} className="bg-white p-3 rounded border">
                          <div className="text-sm font-medium text-gray-500">{key}</div>
                          <div className="text-sm font-semibold text-gray-900 mt-1">{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(product.specifications).map(([key, value]) => (
                  <div key={key} className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm font-medium text-gray-500">{key}</div>
                    <div className="text-lg font-semibold text-gray-900">{value}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {product.technicalData && Object.keys(product.technicalData).length > 0 && (
            <div className="p-8 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Shield className="w-6 h-6 mr-3 text-purple-600" />
                Technical Data
              </h2>
              <div className="space-y-8">
                {Object.entries(product.technicalData).map(([tableName, tableData]) => (
                  <div key={tableName} className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{tableName}</h3>
                    {tableData && tableData.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-white">
                            <tr>
                              {Object.keys(tableData[0]).map((key) => (
                                <th key={key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {tableData.map((row, index) => (
                              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                {Object.values(row).map((value, cellIndex) => (
                                  <td key={cellIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{value}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500">No technical data available.</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {product.testConditions && Object.keys(product.testConditions).length > 0 && (
            <div className="p-8 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Shield className="w-6 h-6 mr-3 text-purple-600" />
                Test Conditions
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(product.testConditions).map(([key, value]) => (
                  <div key={key} className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-sm font-medium text-purple-600">{key}</div>
                    <div className="text-lg font-semibold text-gray-900">{value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {product.applications && product.applications.length > 0 && (
            <div className="p-8 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Droplets className="w-6 h-6 mr-3 text-cyan-600" />
                Applications
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {product.applications.map((application, index) => (
                  <div key={index} className="bg-cyan-50 p-4 rounded-lg">
                    <div className="text-cyan-800 font-medium">{application}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {product.models && product.models.length > 0 && (
            <div className="p-8 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <Zap className="w-6 h-6 mr-3 text-orange-600" />
                Available Models
              </h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model</th>
                      {Object.keys(product.models[0]).slice(1).map((key) => (
                        <th key={key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {product.models.map((model, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{model.model}</td>
                        {Object.entries(model).slice(1).map(([key, value]) => (
                          <td key={key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {typeof value === 'object' ? JSON.stringify(value) : value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {product.processFlow && (
            <div className="p-8 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Process Flow Chart</h2>
              <p className="text-gray-600 mb-4">{product.processFlow.description}</p>
              {product.processFlow.stages && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Process Stages:</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {product.processFlow.stages.map((stage, index) => (
                      <div key={index} className="bg-green-50 p-3 rounded-lg">
                        <div className="text-green-800 font-medium text-sm">{stage}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
                {product.processFlow.components.map((component, index) => (
                  <div key={index} className="bg-blue-50 p-3 rounded-lg text-center">
                    <div className="text-blue-800 font-medium text-sm">{component}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {product.dimensions && product.dimensions.length > 0 && (
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Dimensions</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {product.dimensions.map((dimension, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm font-medium text-gray-500 mb-2">{dimension.model}</div>
                    <div className="space-y-1">
                      {dimension.length && <div className="text-sm text-gray-700"><span className="font-medium">Length:</span> {dimension.length}</div>}
                      {dimension.diameter && <div className="text-sm text-gray-700"><span className="font-medium">Diameter:</span> {dimension.diameter}</div>}
                      {dimension.width && <div className="text-sm text-gray-700"><span className="font-medium">Width:</span> {dimension.width}</div>}
                      {dimension.height && <div className="text-sm text-gray-700"><span className="font-medium">Height:</span> {dimension.height}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;